import { setButtonLabel } from '@atoms/button/utilities';
import { renderSummary } from '@features/area-subscription/components';
import {
    AreaSubscriptionModalElement,
    AreaSubscriptionModalTranslation,
    AreaSubscriptionStatus,
    ExtendedAreaSubscriptionState,
} from '@features/area-subscription/types';
import {
    getListData,
    getSummaryData,
    handleVirtualPageGTM,
    SELECTED_PROJECT_PHASES_COUNT,
} from '@features/area-subscription/utilities';
import { translateItem, translateItems } from '@utilities/hooks';

import { getTranslationOptions, updateTranslationOptions } from '../translation-helpers';

const HIDDEN_CLASS = 'u-hidden';

const modalInterfaceTranslationKeysStatic: AreaSubscriptionModalTranslation = {
    areaOnlyToggleLabel: 'AreaSubscription.Modal.Step1.AreaOnlyToggleLabel',
    step3Title: 'AreaSubscription.Modal.Step3.Title',
    step3ListIntroTitle: 'AreaSubscription.Modal.Step3.AvailableList.IntroTitle',
    step3ListIntroContent: 'AreaSubscription.Modal.Step3.AvailableList.IntroContent',
    step4Title: 'AreaSubscription.Modal.Step4.Title',
    navigationHelperTextProjectsSelect:
        'AreaSubscription.Modal.Step1.NavigationHelperText.ProjectsSelect',
    navigationHelperTextAreaOnly: 'AreaSubscription.Modal.Step1.NavigationHelperText.AreaOnly',
    navigationHelperTextFormValid: 'AreaSubscription.Modal.Step2.NavigationHelperText.FormValid',
    navigationHelperTextFormInvalid:
        'AreaSubscription.Modal.Step2.NavigationHelperText.FormInvalid',
    navigationCtaNextStep: 'AreaSubscription.Modal.NavigationCtaLabel.NextStep',
    navigationCtaClose: 'AreaSubscription.Modal.NavigationCtaLabel.Close',
    navigationCtaCompleteRegistration:
        'AreaSubscription.Modal.NavigationCtaLabel.CompleteRegistration',
};

const modalInterfaceTranslationKeysForStatus: {
    [K in AreaSubscriptionStatus]?: AreaSubscriptionModalTranslation;
} = {
    notSubscribedToArea: {
        step1Title: 'AreaSubscription.SubscribeToArea.Modal.Step1.Title',
        step1Description: 'AreaSubscription.SubscribeToArea.Modal.Step1.Description',
    },
    userNotSubscribedToArea: {
        step1Title: 'AreaSubscription.SubscribeToArea.LoggedInUser.Modal.Step1.Title',
        step1Description: 'AreaSubscription.SubscribeToArea.LoggedInUser.Modal.Step1.Description',
    },
    subscribedToAreaNotSubscribedToProjects: {
        step1Title: 'AreaSubscription.AreaSubscribed.SubscribeToProjects.Modal.Step1.Title',
        step1Description:
            'AreaSubscription.AreaSubscribed.SubscribeToProjects.Modal.Step1.Description',
    },
    subscribedToAreaSubscribedToSomeProjects: {
        step1Title: 'AreaSubscription.AreaSubscribed.SubscribeToMoreProjects.Modal.Step1.Title',
        step1Description:
            'AreaSubscription.AreaSubscribed.SubscribeToMoreProjects.Modal.Step1.Description',
    },
};

const getModalCloseConfirmationTranslations = async () => ({
    title: await translateItem('AreaSubscription.Modal.CloseConfirmationPopup.Title'),
    description: await translateItem('AreaSubscription.Modal.CloseConfirmationPopup.Description'),
    confirmButtonText: await translateItem(
        'AreaSubscription.Modal.CloseConfirmationPopup.CtaConfirm',
    ),
    dismissButtonText: await translateItem(
        'AreaSubscription.Modal.CloseConfirmationPopup.CtaDismiss',
    ),
});

const updateModalElement = async (
    element: AreaSubscriptionModalElement,
    state: ExtendedAreaSubscriptionState,
    options: { step: number; areaOnly: boolean },
) => {
    const modalData = await getModalDataForState(state);

    switch (options.step) {
        case 0: {
            element.step1Title.innerText = modalData.step1Title!;
            element.step1Description.innerHTML = modalData.step1Description!;
            element.areaOnlyToggleLabel.innerHTML = modalData.areaOnlyToggleLabel!;
            element.navigationHelperText.innerHTML = modalData.navigationHelperTextProjectsSelect!;

            if (
                state.status === 'notSubscribedToArea' ||
                state.status === 'userNotSubscribedToArea'
            ) {
                element.areaOnlyToggle.classList.remove(HIDDEN_CLASS);
            } else {
                element.areaOnlyToggle.classList.add(HIDDEN_CLASS);
            }

            const { available, subscribed } = await getListData(state, 'projectPhase');

            if (subscribed?.listData.length) {
                element.step1SubscribedList.classList.remove(HIDDEN_CLASS);
                element.step1SubscribedList.listTitle.innerHTML = subscribed.listTitle!;
                element.step1SubscribedList.setData(subscribed.listData);
            } else {
                element.step1SubscribedList.classList.add(HIDDEN_CLASS);
                element.step1SubscribedList.setData([]);
            }

            if (available?.listData.length) {
                element.step1AvailableList.classList.remove(HIDDEN_CLASS);
                element.step1AvailableList.listTitle.innerHTML = available.listTitle!;
                element.step1AvailableList.selectAllToggleLabel!.innerHTML =
                    available.selectAllToggleLabel!;
                element.step1AvailableList.setData(available.listData);
            } else {
                element.step1AvailableList.classList.add(HIDDEN_CLASS);
                element.step1AvailableList.setData([]);
            }

            if (state.formRequired) {
                setButtonLabel(element.navigationCta, modalData.navigationCtaNextStep!);
            } else {
                setButtonLabel(element.navigationCta, modalData.navigationCtaCompleteRegistration!);
            }
            element.navigationCta.setAttribute('disabled', 'disabled');

            break;
        }
        case 1: {
            setButtonLabel(element.navigationCta, modalData.navigationCtaCompleteRegistration!);
            break;
        }
        case 2: {
            element.step3Title.innerText = modalData.step3Title!;

            const { confirmation, advantages } = await getSummaryData(state, { step: 2 });

            element.step3Confirmation.innerHTML = '';
            element.step3Confirmation.append(renderSummary(confirmation));

            if (advantages) {
                element.step3ListIntroTitle.classList.add(HIDDEN_CLASS);
                element.step3ListIntroContent.classList.add(HIDDEN_CLASS);
                element.step3Advantages.innerHTML = '';
                element.step3Advantages.append(renderSummary(advantages));

                element.step3AvailableList.classList.add(HIDDEN_CLASS);
                element.step3AvailableList.setData([]);
            } else {
                element.step3Advantages.innerHTML = '';
                element.step3ListIntroTitle.innerHTML = modalData.step3ListIntroTitle!;
                element.step3ListIntroContent.innerHTML = modalData.step3ListIntroContent!;
                element.step3ListIntroTitle.classList.remove(HIDDEN_CLASS);
                element.step3ListIntroContent.classList.remove(HIDDEN_CLASS);

                const { available } = await getListData(state, 'propertyType');

                if (available?.listData.length) {
                    element.step3AvailableList.classList.remove(HIDDEN_CLASS);
                    element.step3AvailableList.setData(available.listData);
                } else {
                    element.step3AvailableList.classList.add(HIDDEN_CLASS);
                    element.step3AvailableList.setData([]);
                    element.step3ListIntroTitle.classList.add(HIDDEN_CLASS);
                    element.step3ListIntroContent.classList.add(HIDDEN_CLASS);
                }
            }

            element.navigationHelperText.innerHTML = '';
            element.navigationCheckmark.classList.add(HIDDEN_CLASS);
            element.navigationCta.removeAttribute('disabled');
            setButtonLabel(element.navigationCta, modalData.navigationCtaClose!);

            break;
        }
        case 3: {
            element.step4Title.innerText = modalData.step4Title!;

            const { confirmation, advantages } = await getSummaryData(state, { step: 3 });

            element.step4Confirmation.innerHTML = '';
            element.step4Confirmation.append(renderSummary(confirmation));
            if (advantages) {
                element.step4Advantages.innerHTML = '';
                element.step4Advantages.append(renderSummary(advantages));
            } else {
                element.step4Advantages.innerHTML = '';
            }

            element.navigationHelperText.innerHTML = '';
            element.navigationCheckmark.classList.add(HIDDEN_CLASS);
            element.navigationCta.removeAttribute('disabled');
            setButtonLabel(element.navigationCta, modalData.navigationCtaClose!);
            break;
        }
        default:
            break;
    }

    element.bpdPages.setPage(String(options.step));

    if (element.modal.active) {
        await handleVirtualPageGTM({ step: element.bpdPages.selected, areaOnly: options.areaOnly });
    }

    return modalData;
};

const getModalDataForState = async ({
    status,
}: ExtendedAreaSubscriptionState): Promise<AreaSubscriptionModalTranslation> => {
    const modalTranslationKeys = {
        ...modalInterfaceTranslationKeysStatic,
        ...(status && modalInterfaceTranslationKeysForStatus[status]),
    };

    return await translateItems(modalTranslationKeys, getTranslationOptions());
};

const getHelperTextForSelection = async ({
    step,
    selected,
    total,
}: {
    step: number;
    selected?: number;
    total?: number;
}): Promise<AreaSubscriptionModalTranslation> => {
    switch (step) {
        case 0: {
            const { navigationHelperTextProjectsSelect, navigationHelperTextAreaOnly } =
                modalInterfaceTranslationKeysStatic;

            const oldTranslation = getTranslationOptions().plurality?.find(
                ({ id }) => id === SELECTED_PROJECT_PHASES_COUNT,
            );

            const stagedTranslateOptions = {
                replace: [{ id: SELECTED_PROJECT_PHASES_COUNT, value: `${selected || 0}` }],
                plurality: [
                    {
                        id: SELECTED_PROJECT_PHASES_COUNT,
                        value: selected || 0,
                        total: total ?? oldTranslation?.total,
                    },
                ],
            };

            return await translateItems(
                { navigationHelperTextProjectsSelect, navigationHelperTextAreaOnly },
                updateTranslationOptions(stagedTranslateOptions),
            );
        }
        default: {
            return { navigationHelperTextProjectsSelect: '', navigationHelperTextAreaOnly: '' };
        }
    }
};

export { updateModalElement, getHelperTextForSelection, getModalCloseConfirmationTranslations };
